/**
 * Variant Selector Helper
 * 
 * This script automatically does the following:
 * 1. For options with only 1 variant: automatically selects it and hides the option section
 * 2. For options with multiple variants: leaves them unselected for the customer to choose
 * 
 * Enhanced version that works on all contexts including:
 * - Product detail pages
 * - Collection page quick-add/popup forms
 * - Upsell popups and modals
 * - Any dynamically loaded variant selector
 */

(function() {
  class VariantSelectorHandler {
    constructor() {
      this.init();
    }

    init() {
      // Run on page load
      document.addEventListener('DOMContentLoaded', () => {
        this.handleVariantOptions();
        this.setupMutationObserver();
      });
      
      // Also run when the page content is updated (for quick view or other dynamic loading)
      document.addEventListener('shopify:section:load', () => {
        this.handleVariantOptions();
      });
      
      // Run when Shopify events related to product forms occur
      document.addEventListener('shopify:block:select', () => {
        this.handleVariantOptions();
      });
      
      // Additional custom events that might be triggered by theme JS
      document.addEventListener('quickview:loaded', () => {
        this.handleVariantOptions();
      });
      
      document.addEventListener('modal:opened', () => {
        this.handleVariantOptions();
      });
    }
    
    // Set up a mutation observer to detect dynamically added variant selectors
    setupMutationObserver() {
      const observer = new MutationObserver((mutations) => {
        let shouldProcess = false;
        
        mutations.forEach(mutation => {
          // Look for added nodes that might contain variant selectors
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (let i = 0; i < mutation.addedNodes.length; i++) {
              const node = mutation.addedNodes[i];
              
              // Only process element nodes
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if this node or its children might contain variant selectors
                if (node.classList && (
                    node.classList.contains('modal') ||
                    node.classList.contains('quickview') ||
                    node.classList.contains('product-form') ||
                    node.querySelector('.options-selection__option-values')
                  )) {
                  shouldProcess = true;
                  break;
                }
              }
            }
          }
        });
        
        if (shouldProcess) {
          // Wait a brief moment for any JS to initialize the form
          setTimeout(() => this.handleVariantOptions(), 50);
        }
      });
      
      // Observe the entire document for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    handleVariantOptions() {
      // Find all option sections, including those in popups and modals
      const optionSections = document.querySelectorAll('.options-selection__option-values, .modal .option-values, .quickview .option-values, [data-product-form] .option-values');
      
      if (optionSections.length === 0) {
        // If no option sections found with standard selectors, try alternative selectors that might be used in popups
        const productForms = document.querySelectorAll('.product-form, .product_form, form[action*="/cart/add"]');
        productForms.forEach(form => {
          const potentialOptionSections = form.querySelectorAll('fieldset, .selector-wrapper, .swatch');
          potentialOptionSections.forEach(section => this.processOptionSection(section));
        });
      }
      
      // Process each option section that was found with the main selectors
      optionSections.forEach(section => this.processOptionSection(section));

      // Also look for any standalone variant selectors that might be in popups
      this.handleStandaloneSelectors();
    }
    
    processOptionSection(section) {
      // Find option values within this section
      const optionValues = section.querySelectorAll('.options-selection__option-value, .option-value, input[type="radio"][name*="option"]');
      
      if (optionValues.length === 0) return;
      
      // If there's only one option value, hide this entire option section and select it by default
      if (optionValues.length === 1) {
        // Get the radio input inside the only option value
        let radioInput = optionValues[0].querySelector('input[type="radio"]');
        
        // If the option value itself is a radio input (for different selector structures)
        if (!radioInput && optionValues[0].tagName === 'INPUT' && optionValues[0].type === 'radio') {
          radioInput = optionValues[0];
        }
        
        if (radioInput) {
          // Select this option by default (check it)
          radioInput.checked = true;
          
          // Trigger change event to update the product state
          radioInput.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Find the parent container and hide it
          // Try various parent selectors that might be used in different contexts
          const parentContainer = section.closest('fieldset, .selector-wrapper, .swatch, .product-option');
          if (parentContainer) {
            parentContainer.style.display = 'none';
          } else {
            // If no specific parent found, try to hide the section itself
            section.style.display = 'none';
          }
        }
      } else {
        // More than one option - ensure nothing is pre-selected (unless already selected by the customer)
        const radioInputs = section.querySelectorAll('input[type="radio"]');
        let anyChecked = false;
        
        // Check if any options are already selected by the user
        radioInputs.forEach(radio => {
          if (radio.checked) anyChecked = true;
        });
        
        // If nothing is selected, leave it that way for user to choose
        if (!anyChecked) {
          radioInputs.forEach(radio => {
            radio.checked = false;
          });
        }
      }
    }
    
    handleStandaloneSelectors() {
      // Handle selects that might be used in popups/upsells
      const selectOptions = document.querySelectorAll('select[name*="option"]');
      
      selectOptions.forEach(select => {
        if (select.options.length === 2) { // Account for the first option often being a placeholder
          const firstNonPlaceholderIndex = select.options[0].value === '' ? 1 : 0;
          
          // If only one real option in the dropdown, select it and hide the select
          if (firstNonPlaceholderIndex === 1 && select.options.length === 2) {
            select.selectedIndex = 1; // Select the only real option
            select.dispatchEvent(new Event('change', { bubbles: true }));
            
            // Hide the parent container if possible
            const parentContainer = select.closest('.selector-wrapper, .product-option');
            if (parentContainer) {
              parentContainer.style.display = 'none';
            } else {
              // Otherwise hide the select itself
              select.style.display = 'none';
            }
          }
        }
      });
    }
  }

  // Initialize the handler
  new VariantSelectorHandler();
})();
